import type { Permission, TenantResponse, UserAudience } from '@contract-analyzer/api-client';
import {
  ArrowLeftIcon,
  BarChart3Icon,
  BookOpenIcon,
  BriefcaseIcon,
  CheckIcon,
  CheckSquareIcon,
  FileTextIcon,
  FolderOpenIcon,
  HelpCircleIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  MailIcon,
  MoonIcon,
  SettingsIcon,
  ShieldIcon,
  SunIcon,
  UsersIcon,
} from 'lucide-react';
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { AuthUser } from '@/hooks/auth-context';
import {
  AgentConnectionMenuItem,
  SidebarAgentConnectedMark,
  SidebarAgentInvitation,
} from '@/components/agent-connection/agent-connection-entry-points';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Sidebar,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import {
  AppSidebarBody,
  AppSidebarFooter,
  AppSidebarHeader,
  SidebarNavSection,
  SidebarProjectRow,
  SidebarUploadAction,
  SidebarUserMenuButton,
  SidebarWorkspaceButton,
  SidebarWorkspaceName,
  type NavItem,
} from '@/components/layout/app-sidebar-sections';
import { DemoModeMenuItem } from '@/components/layout/demo-mode-menu-item';
import { SidebarUsageWidget } from '@/components/layout/sidebar-usage-widget';
import { ReleaseNotesMenuItem } from '@/components/release-news/release-notes-menu-item';
import { ProjectCtx } from '@/hooks/project-context';
import { TenantCtx } from '@/hooks/tenant-context';
import { useAuth } from '@/hooks/use-auth';
import { useProjectCapabilities } from '@/hooks/use-project-capabilities';
import { useProjectPermissions } from '@/hooks/use-project-permissions';
import { useUserPhoto } from '@/hooks/use-user-photo';
import { useTheme } from '@/hooks/use-theme';
import { apiClient } from '@/lib/api';
import { projectPath, tenantPath } from '@/lib/routes';

const USERS_MANAGE_PERMISSION = 'users.manage';
const EMPTY_PROJECT_PERMISSIONS: readonly Permission[] = [];

interface WorkspaceOption {
  readonly key: string;
  readonly tenantSlug: string;
  readonly tenantDisplayName: string;
}

function canManageUsers(user: AuthUser | undefined): boolean {
  return user?.is_superadmin === true || user?.permissions.includes(USERS_MANAGE_PERMISSION) === true;
}

function formatAccessLabel(user: AuthUser | null): string {
  if (user === null) {
    return 'guest';
  }

  if (user.is_superadmin) {
    return 'platform admin';
  }

  if (user.permissions.includes(USERS_MANAGE_PERMISSION)) {
    return 'workspace admin';
  }

  return 'member';
}

const USER_MANUAL_FILENAMES: Record<UserAudience, string> = {
  internal: 'Internal User Manual EYQ Contract Analyzer_v_2.0.1.0.pdf',
  external: 'External User Manual EYQ Contract Analyzer_v_2.0.1.0.pdf',
};

function resolveUserManualHref(audience: UserAudience): string {
  return `/user-manual/${encodeURIComponent(USER_MANUAL_FILENAMES[audience])}`;
}

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.replace(/\/+$/, '');
  }

  return path;
}

function matchesPath(pathname: string, href: string, match: 'exact' | 'prefix'): boolean {
  const normalizedPathname = normalizePath(pathname);
  const normalizedHref = normalizePath(href);

  if (match === 'exact') {
    return normalizedPathname === normalizedHref;
  }

  return (
    normalizedPathname === normalizedHref || normalizedPathname.startsWith(`${normalizedHref}/`)
  );
}

function ProjectPermissionLoadingSection() {
  return (
    <SidebarGroup>
      <SidebarGroupLabel>Project navigation</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu aria-label="Project navigation loading">
          <SidebarMenuItem>
            <SidebarMenuSkeleton showIcon />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuSkeleton showIcon />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuSkeleton showIcon />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const tenant = useContext(TenantCtx);
  const project = useContext(ProjectCtx);
  const { setTheme, theme } = useTheme();
  const {
    availableMemberships,
    hasPermission,
    logout,
    pendingInvitations,
    pendingPlatformInvitations,
    user,
  } = useAuth();
  const [loadedSuperadminTenantUserId, setLoadedSuperadminTenantUserId] = useState<string | null>(null);
  const [loadingSuperadminTenantUserId, setLoadingSuperadminTenantUserId] = useState<string | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isWorkspaceMenuOpen, setIsWorkspaceMenuOpen] = useState(false);
  const [isHeaderWorkspaceMenuOpen, setIsHeaderWorkspaceMenuOpen] = useState(false);
  const [workspaceSearchValue, setWorkspaceSearchValue] = useState('');
  const [superadminTenants, setSuperadminTenants] = useState<readonly TenantResponse[]>([]);
  const projectId = project?.project.project_id ?? null;
  const { capabilities: projectCapabilities, markAnnounced } = useProjectCapabilities(projectId);

  const resolvedUser = user ?? undefined;
  const isSuperadminWithoutWorkspace = user?.is_superadmin === true && tenant === null;
  const currentSuperadminUserId = user?.is_superadmin === true ? user.user_id : null;
  const currentWorkspaceSlug = tenant?.slug ?? user?.tenant_slug ?? null;
  const pathname = normalizePath(location.pathname);
  const hasLoadedSuperadminTenants =
    currentSuperadminUserId !== null && loadedSuperadminTenantUserId === currentSuperadminUserId;
  const isLoadingSuperadminTenants =
    currentSuperadminUserId !== null && loadingSuperadminTenantUserId === currentSuperadminUserId;
  const workspaceMemberships = useMemo(
    () => availableMemberships.filter((membership) => membership.is_active),
    [availableMemberships],
  );
  const {
    error: projectPermissionsError,
    isLoading: projectPermissionsLoading,
    permissions: projectPermissions,
  } = useProjectPermissions(projectId);
  const effectiveProjectPermissions =
    projectPermissionsError === null ? projectPermissions : EMPTY_PROJECT_PERMISSIONS;
  const normalizedWorkspaceSearch = workspaceSearchValue.trim().toLocaleLowerCase();
  const workspaceOptions = useMemo<readonly WorkspaceOption[]>(() => {
    const optionsByTenantId = new Map<string, WorkspaceOption>();

    for (const membership of workspaceMemberships) {
      optionsByTenantId.set(membership.tenant_id, {
        key: membership.membership_id,
        tenantSlug: membership.tenant_slug,
        tenantDisplayName: membership.tenant_display_name,
      });
    }

    if (currentSuperadminUserId !== null) {
      for (const tenantRecord of superadminTenants) {
        if (!tenantRecord.is_active || optionsByTenantId.has(tenantRecord.tenant_id)) {
          continue;
        }

        optionsByTenantId.set(tenantRecord.tenant_id, {
          key: `tenant-${tenantRecord.tenant_id}`,
          tenantSlug: tenantRecord.slug,
          tenantDisplayName: tenantRecord.display_name,
        });
      }
    }

    return [...optionsByTenantId.values()].sort((left, right) =>
      left.tenantDisplayName.localeCompare(right.tenantDisplayName),
    );
  }, [currentSuperadminUserId, superadminTenants, workspaceMemberships]);
  const filteredWorkspaceOptions = useMemo(() => {
    if (normalizedWorkspaceSearch.length === 0) {
      return workspaceOptions;
    }

    return workspaceOptions.filter((workspace) => {
      const displayName = workspace.tenantDisplayName.toLocaleLowerCase();
      const slug = workspace.tenantSlug.toLocaleLowerCase();
      return (
        displayName.includes(normalizedWorkspaceSearch) || slug.includes(normalizedWorkspaceSearch)
      );
    });
  }, [normalizedWorkspaceSearch, workspaceOptions]);
  const fallbackWorkspaceSlug = workspaceOptions[0]?.tenantSlug ?? null;
  const workspaceReturnSlug = currentWorkspaceSlug ?? fallbackWorkspaceSlug;

  const noTenantItems = useMemo(() => {
    const items: NavItem[] = [];

    // When viewing a global route (/admin, /settings) while the user still has a
    // workspace available, surface an explicit link back to that workspace so
    // the sidebar is never a dead end.
    if (workspaceReturnSlug !== null) {
      items.push({
        href: tenantPath(workspaceReturnSlug),
        icon: ArrowLeftIcon,
        label: 'Back to workspace',
        match: 'none',
      });
    }

    if (resolvedUser?.is_superadmin === true) {
      items.push({ href: '/control-plane', icon: BarChart3Icon, label: 'Control plane', match: 'exact' });
      items.push({ href: '/admin', icon: ShieldIcon, label: 'Platform', match: 'exact' });
    }

    items.push({ href: '/settings', icon: SettingsIcon, label: 'Settings', match: 'exact' });

    return items;
  }, [resolvedUser?.is_superadmin, workspaceReturnSlug]);

  const tenantSections = useMemo(() => {
    if (tenant === null) {
      return [] as const;
    }

    const tenantRootHref = tenantPath(tenant.slug);
    const tenantProjectsHref = tenantPath(tenant.slug, '/projects');
    const hasWorkspaceProjectAccess = canManageUsers(resolvedUser);

    const primaryItems: NavItem[] = [
      {
        href: tenantRootHref,
        icon: LayoutDashboardIcon,
        label: 'Dashboard',
        match: 'exact',
        folioTargetId: 'tenant-sidebar-dashboard-link',
      },
      {
        href: tenantProjectsHref,
        icon: FolderOpenIcon,
        label: 'Projects',
        match: 'exact',
        folioTargetId: 'tenant-sidebar-projects-link',
      },
    ];

    if (hasWorkspaceProjectAccess || hasPermission('templates.view')) {
      primaryItems.push({
        href: tenantPath(tenant.slug, '/templates'),
        icon: BookOpenIcon,
        label: 'Template library',
        folioTargetId: 'tenant-sidebar-templates-link',
      });
    }

    const workItems: NavItem[] = [];

    const settingsItems: NavItem[] = [
      {
        href: tenantPath(tenant.slug, '/settings'),
        icon: SettingsIcon,
        label: 'Settings',
        folioTargetId: 'tenant-sidebar-settings-link',
      },
    ];

    if (resolvedUser?.is_superadmin === true) {
      settingsItems.unshift({ href: '/control-plane', icon: BarChart3Icon, label: 'Control plane', match: 'exact' });
      settingsItems.unshift({ href: '/admin', icon: ShieldIcon, label: 'Platform', match: 'exact' });
    }

    if (canManageUsers(resolvedUser)) {
      settingsItems.unshift({
        href: tenantPath(tenant.slug, '/members'),
        icon: UsersIcon,
        label: 'Members',
        folioTargetId: 'tenant-sidebar-members-link',
      });
    }

    return [primaryItems, workItems, settingsItems] as const;
  }, [hasPermission, resolvedUser, tenant]);

  const projectSections = useMemo(() => {
    if (tenant === null || project === null) {
      return [] as const;
    }

    const projectRootHref = projectPath(tenant.slug, project.project.project_id);
    const canViewProjectDocuments = effectiveProjectPermissions.includes('documents.view');
    const canViewProjectTemplates = effectiveProjectPermissions.includes('templates.view');
    const canViewProjectRuns = effectiveProjectPermissions.includes('runs.view');
    const canViewProjectAnalyses = effectiveProjectPermissions.includes('analyses.view');
    const canEditProjectSettings = effectiveProjectPermissions.includes('projects.edit');
    const projectItems: NavItem[] = [
      {
        href: projectRootHref,
        icon: LayoutDashboardIcon,
        label: 'Overview',
        match: 'exact',
        folioTargetId: 'project-sidebar-overview-link',
      },
    ];

    if (canViewProjectDocuments) {
      projectItems.push({
        href: projectPath(tenant.slug, project.project.project_id, '/documents'),
        icon: FileTextIcon,
        label: 'Documents',
        folioTargetId: 'project-sidebar-documents-link',
      });
    }

    if (canViewProjectTemplates) {
      projectItems.push({
        href: projectPath(tenant.slug, project.project.project_id, '/templates'),
        icon: BookOpenIcon,
        label: 'Templates',
        folioTargetId: 'project-sidebar-templates-link',
      });
    }

    if (canViewProjectRuns && projectCapabilities.verify) {
      projectItems.push({
        href: projectPath(tenant.slug, project.project.project_id, '/evaluations'),
        icon: CheckSquareIcon,
        label: 'Verify',
        activeHrefs: [
          projectPath(tenant.slug, project.project.project_id, '/evaluations'),
          projectPath(tenant.slug, project.project.project_id, '/ground-truth'),
        ],
        folioTargetId: 'project-sidebar-verify-link',
        ...(projectCapabilities.verifyAnnounced
          ? {}
          : {
              onPopInEnd: () => {
                markAnnounced('verify');
              },
            }),
      });
    }

    if (projectCapabilities.tsa_workspace) {
      projectItems.push({
        href: projectPath(tenant.slug, project.project.project_id, '/tsa-workspace'),
        icon: BriefcaseIcon,
        label: 'TSA Workspace',
        folioTargetId: 'project-sidebar-tsa-workspace-link',
        ...(projectCapabilities.tsaWorkspaceAnnounced
          ? {}
          : {
              onPopInEnd: () => {
                markAnnounced('tsa_workspace');
              },
            }),
      });
    }

    const projectUtilityItems: NavItem[] = [];

    if (canManageUsers(resolvedUser)) {
      projectUtilityItems.push({
        href: projectPath(tenant.slug, project.project.project_id, '/members'),
        icon: UsersIcon,
        label: 'Project members',
        folioTargetId: 'project-sidebar-members-link',
      });
    }

    // General / Automation / Trash need `projects.edit`; the read-only
    // execution log remains reachable with run or analysis view access.
    const canOpenProjectSettings =
      canEditProjectSettings || canViewProjectRuns || canViewProjectAnalyses;
    if (canOpenProjectSettings) {
      projectUtilityItems.push({
        href: projectPath(tenant.slug, project.project.project_id, '/settings'),
        icon: SettingsIcon,
        label: 'Project settings',
        activeHrefs: [
          projectPath(tenant.slug, project.project.project_id, '/settings'),
          projectPath(tenant.slug, project.project.project_id, '/runs'),
          projectPath(tenant.slug, project.project.project_id, '/analyses'),
        ],
        folioTargetId: 'project-sidebar-project-settings-link',
      });
    }

    return [projectItems, projectUtilityItems] as const;
  }, [
    project,
    projectCapabilities.verify,
    projectCapabilities.verifyAnnounced,
    projectCapabilities.tsa_workspace,
    projectCapabilities.tsaWorkspaceAnnounced,
    effectiveProjectPermissions,
    markAnnounced,
    resolvedUser,
    tenant,
  ]);

  const uploadHref =
    tenant !== null && project !== null
      ? projectPath(tenant.slug, project.project.project_id, '/upload-documents')
      : null;
  const canUploadDocuments = effectiveProjectPermissions.includes('documents.add');
  const showProjectPermissionSkeletons =
    projectPermissionsLoading && projectPermissions.length === 0;

  const isActive = useCallback(
    (item: NavItem): boolean => {
      if (item.match === 'none') {
        return false;
      }

      const hrefs = item.activeHrefs ?? [item.href];
      const matchMode = item.match ?? 'prefix';
      return hrefs.some((href) => matchesPath(pathname, href, matchMode));
    },
    [pathname],
  );

  const handleWorkspaceSelect = useCallback(
    (workspace: WorkspaceOption) => {
      void navigate(tenantPath(workspace.tenantSlug));
    },
    [navigate],
  );

  const loadSuperadminTenants = useCallback(() => {
    if (
      currentSuperadminUserId === null ||
      hasLoadedSuperadminTenants ||
      isLoadingSuperadminTenants
    ) {
      return;
    }

    const requestedUserId = currentSuperadminUserId;
    setLoadingSuperadminTenantUserId(requestedUserId);

    void apiClient
      .listTenants()
      .then((tenantRecords) => {
        setSuperadminTenants(tenantRecords.filter((tenantRecord) => tenantRecord.is_active));
        setLoadedSuperadminTenantUserId(requestedUserId);
      })
      .catch(() => {
        setSuperadminTenants([]);
        setLoadedSuperadminTenantUserId(requestedUserId);
      })
      .finally(() => {
        setLoadingSuperadminTenantUserId((currentUserId) =>
          currentUserId === requestedUserId ? null : currentUserId,
        );
      });
  }, [currentSuperadminUserId, hasLoadedSuperadminTenants, isLoadingSuperadminTenants]);

  const handleUserMenuOpenChange = useCallback((open: boolean) => {
    setIsUserMenuOpen(open);
    if (open) {
      return;
    }

    setIsWorkspaceMenuOpen(false);
    setWorkspaceSearchValue('');
  }, []);

  const handleWorkspaceMenuOpenChange = useCallback((open: boolean) => {
    setIsWorkspaceMenuOpen(open);
    if (open) {
      loadSuperadminTenants();
      return;
    }

    setWorkspaceSearchValue('');
  }, [loadSuperadminTenants]);

  const handleHeaderWorkspaceMenuOpenChange = useCallback((open: boolean) => {
    setIsHeaderWorkspaceMenuOpen(open);
    if (open) {
      loadSuperadminTenants();
      return;
    }

    setWorkspaceSearchValue('');
  }, [loadSuperadminTenants]);

  const handleWorkspaceSearchKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'ArrowUp':
        case 'Enter':
        case 'Escape':
        case 'Tab':
          return;
        default:
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
      }
    },
    [],
  );

  function handleLogout() {
    void logout();
    void navigate('/login');
  }

  const displayName = user?.display_name ?? 'User';
  const userRole = formatAccessLabel(user);
  const userEmail = user?.email ?? '';
  const showWorkspaceMenu = workspaceOptions.length > 0 || user?.is_superadmin === true;
  const photoUrl = useUserPhoto(user !== null);

  return (
    <Sidebar collapsible="icon">
      <AppSidebarHeader
        workspace={
          tenant !== null ? (
            showWorkspaceMenu ? (
              <DropdownMenu
                open={isHeaderWorkspaceMenuOpen}
                onOpenChange={handleHeaderWorkspaceMenuOpenChange}
              >
                <DropdownMenuTrigger asChild>
                  <SidebarWorkspaceButton workspaceName={tenant.displayName} />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-(--radix-dropdown-menu-trigger-width) min-w-72"
                >
                  <DropdownMenuLabel className="text-micro font-medium text-muted-foreground">
                    Switch workspace
                  </DropdownMenuLabel>
                  <div className="sticky top-0 bg-popover px-1 pb-1">
                    <Input
                      aria-label="Search workspaces"
                      autoFocus
                      className="h-9"
                      onChange={(event) => {
                        setWorkspaceSearchValue(event.target.value);
                      }}
                      onKeyDownCapture={handleWorkspaceSearchKeyDown}
                      placeholder="Search workspaces"
                      value={workspaceSearchValue}
                    />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    {isLoadingSuperadminTenants ? (
                      <DropdownMenuItem disabled>Loading workspaces…</DropdownMenuItem>
                    ) : workspaceOptions.length === 0 ? (
                      <DropdownMenuItem disabled>No active workspaces</DropdownMenuItem>
                    ) : filteredWorkspaceOptions.length === 0 ? (
                      <DropdownMenuItem disabled>No matching workspaces</DropdownMenuItem>
                    ) : filteredWorkspaceOptions.map((workspace) => {
                      const isCurrentWorkspace = workspace.tenantSlug === currentWorkspaceSlug;
                      return (
                        <DropdownMenuItem
                          key={workspace.key}
                          onClick={() => {
                            handleWorkspaceSelect(workspace);
                          }}
                        >
                          <span className="flex flex-1 items-center gap-2 truncate">
                            <span className="truncate">{workspace.tenantDisplayName}</span>
                          </span>
                          {isCurrentWorkspace ? <CheckIcon className="text-primary" /> : null}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <SidebarWorkspaceName workspaceName={tenant.displayName} />
            )
          ) : isSuperadminWithoutWorkspace ? (
            <p className="px-2 text-xs font-medium text-muted-foreground">No active workspace</p>
          ) : null
        }
        project={
          tenant !== null && project !== null ? (
            <SidebarProjectRow
              backHref={tenantPath(tenant.slug, '/projects')}
              displayName={project.project.display_name}
              iconUrl={project.project.icon_url}
            />
          ) : null
        }
      />

      <AppSidebarBody agentInvitation={<SidebarAgentInvitation />} usage={<SidebarUsageWidget />}>
        {tenant !== null && project !== null ? (
          <>
            {uploadHref !== null && canUploadDocuments ? (
              <>
                <SidebarUploadAction href={uploadHref} />
                <SidebarSeparator />
              </>
            ) : null}

            {showProjectPermissionSkeletons ? (
              <ProjectPermissionLoadingSection />
            ) : (
              <SidebarNavSection
                ariaLabel="Project navigation"
                items={projectSections[0] ?? []}
                isActive={isActive}
                label="Project navigation"
              />
            )}

            {(projectSections[1]?.length ?? 0) > 0 ? <SidebarSeparator /> : null}

            <SidebarNavSection
              ariaLabel="Tenant utilities"
              items={projectSections[1] ?? []}
              isActive={isActive}
              label="Tenant utilities"
            />
          </>
        ) : tenant !== null ? (
          <>
            <SidebarNavSection
              ariaLabel="Tenant primary navigation"
              items={tenantSections[0] ?? []}
              isActive={isActive}
              label="Tenant primary navigation"
            />

            {(tenantSections[1]?.length ?? 0) > 0 ? <SidebarSeparator /> : null}

            <SidebarNavSection
              ariaLabel="Tenant work navigation"
              items={tenantSections[1] ?? []}
              isActive={isActive}
              label="Tenant work navigation"
            />

            {(tenantSections[2]?.length ?? 0) > 0 ? <SidebarSeparator /> : null}

            <SidebarNavSection
              ariaLabel="Tenant settings navigation"
              items={tenantSections[2] ?? []}
              isActive={isActive}
              label="Tenant settings navigation"
            />
          </>
        ) : (
          <SidebarNavSection
            ariaLabel="Global navigation"
            items={noTenantItems}
            isActive={isActive}
            label="Global navigation"
          />
        )}
      </AppSidebarBody>

      <AppSidebarFooter
        agentStatus={<SidebarAgentConnectedMark />}
        userMenu={
          <DropdownMenu open={isUserMenuOpen} onOpenChange={handleUserMenuOpenChange}>
            <DropdownMenuTrigger asChild>
              <SidebarUserMenuButton displayName={displayName} photoUrl={photoUrl} role={userRole} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56" side="top">
              <DropdownMenuLabel>{userEmail}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {showWorkspaceMenu ? (
                <>
                  <DropdownMenuSub
                    open={isWorkspaceMenuOpen}
                    onOpenChange={handleWorkspaceMenuOpenChange}
                  >
                    <DropdownMenuSubTrigger
                      data-folio-target="sidebar-user-menu-workspaces"
                      onClick={(event) => {
                        event.preventDefault();
                        handleWorkspaceMenuOpenChange(true);
                      }}
                    >
                      Workspaces
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="w-72">
                      <div className="sticky top-0 bg-popover px-1 pb-1">
                        <Input
                          aria-label="Search workspaces"
                          autoFocus
                          className="h-9"
                          onChange={(event) => {
                            setWorkspaceSearchValue(event.target.value);
                          }}
                          onKeyDownCapture={handleWorkspaceSearchKeyDown}
                          placeholder="Search workspaces"
                          value={workspaceSearchValue}
                        />
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {isLoadingSuperadminTenants ? (
                          <DropdownMenuItem disabled>Loading workspaces…</DropdownMenuItem>
                        ) : workspaceOptions.length === 0 ? (
                          <DropdownMenuItem disabled>No active workspaces</DropdownMenuItem>
                        ) : filteredWorkspaceOptions.length === 0 ? (
                          <DropdownMenuItem disabled>No matching workspaces</DropdownMenuItem>
                        ) : filteredWorkspaceOptions.map((workspace) => {
                          const isCurrentWorkspace = workspace.tenantSlug === currentWorkspaceSlug;
                          return (
                            <DropdownMenuItem
                              key={workspace.key}
                              onClick={() => {
                                handleWorkspaceSelect(workspace);
                              }}
                            >
                              <span className="flex flex-1 items-center gap-2 truncate">
                                <span className="truncate">{workspace.tenantDisplayName}</span>
                              </span>
                              {isCurrentWorkspace ? <CheckIcon className="text-primary" /> : null}
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                </>
              ) : null}
              <DropdownMenuGroup>
                {pendingInvitations.length + pendingPlatformInvitations.length > 0 ? (
                  <DropdownMenuItem
                    onClick={() => {
                      void navigate('/invitations');
                    }}
                  >
                    <MailIcon data-icon="inline-start" />
                    Invitations
                    <Badge className="ml-auto" variant="secondary">
                      {pendingInvitations.length + pendingPlatformInvitations.length}
                    </Badge>
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  onClick={() => {
                    setTheme(theme === 'dark' ? 'light' : 'dark');
                  }}
                >
                  {theme === 'dark' ? (
                    <SunIcon data-icon="inline-start" />
                  ) : (
                    <MoonIcon data-icon="inline-start" />
                  )}
                  {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                </DropdownMenuItem>
                <AgentConnectionMenuItem />
                <DemoModeMenuItem />
                {resolvedUser === undefined ? null : (
                  <DropdownMenuItem asChild>
                    <a
                      data-folio-target="sidebar-user-menu-user-manual"
                      href={resolveUserManualHref(resolvedUser.user_audience)}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <HelpCircleIcon data-icon="inline-start" />
                      User manual
                    </a>
                  </DropdownMenuItem>
                )}
                <ReleaseNotesMenuItem />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOutIcon data-icon="inline-start" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
    </Sidebar>
  );
}
